/* eslint-disable react/jsx-no-target-blank */
import React, { useState, useEffect, useRef } from "react";

import SwiperCore, { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import classnames from "classnames";
import usePDPStore, {
  useActiveProductData,
  useConfiguringPendingProductData,
  useActiveConfigurationSKU,
  useActiveUniqueMaterial
} from "@store/pdp";
import useContentStore from "@store/content";
import useGlobalStore from "@store/global";
import { useTranslation } from "react-i18next";

import PDPProductImageSpinner from "../PDPProductImageSpinner";
import PDPProductImageZoom from "../PDPProductImageZoom";
import PDPDownloadButton from "../PDPDownloadButton";
import { ArrowSmallSvg } from "@assets/js/icons";
import getUniqueId from "@assets/js/get-unique-id.js";

import { useActiveSelector, useConfiguratorMode } from "@store/pdp";
import { placeholderReplace, log } from "@assets/js/utils";
import dataLayerPush from "@assets/js/gtm";
import { CONFIGURATOR_MODE, CONFIGURATOR_SELECTOR } from "@constants";

import "./pdp-product-images.scss";

const modelImageUrl = `${process.env.REACT_APP_PRODUCT_IMAGE_API}r/`;
const modelImageNoCDNUrl = `${process.env.REACT_APP_PRODUCT_NOCDN_IMAGE_API}r/`;

// install Swiper components
SwiperCore.use([Navigation]);

const PDPProductImages = ({ onSliderChange, skuImageUrl }) => {
  const webPSupport = useGlobalStore((state) => state.webPSupport);
  const imageData = usePDPStore((state) => state.productImage);
  const [configuratorMode] = useConfiguratorMode();
  //const [pendingProductData] = usePendingProductData();
  const [activeProductData] = useActiveProductData();
  const [activeConfigurationSKU] = useActiveConfigurationSKU();
  const [configuringPendingProductData] = useConfiguringPendingProductData();
  //const [activeModelData, setActiveModelData] = useActiveModelData(null);
  const setShowQRCodeModal = useContentStore((store) => store.setShowQRCodeModal);
  const [activeUniqueMaterial] = useActiveUniqueMaterial();
  const [zoom, setZoom] = useState({ zooming: false, event: null });
  const [images, setImages] = useState([]);
  const [imageProgress, setImageProgress] = useState(0);
  const [activeSelector] = useActiveSelector();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeStyledImage, setActiveStyledImage] = useState(null);
  const [swiperInitialized, setSwiperInitialized] = useState(false);
  const swiperInstance = useRef();
  const { t } = useTranslation();
  const texts = {
    addToWishList: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_AddToWishlist"
    ),
    clickToZoom: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_ClickToZoom"
    ),
    dragToRotate: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_DragToRotate"
    ),
    webVr: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_WebVr"),
    webArText: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_WebArText"),
  };
  const [isRelAR, setIsRelAr] = useState(false);
  const [webArUrl, setWebarUrl] = useState(false);
  const [styledImageIsActive, setStyledImageIsActive] = useState(false);

  useEffect(() => {
    setZoom({ zooming: false });
  }, [activeSelector, configuringPendingProductData]);

  useEffect(() => {
    if (
      configuringPendingProductData?.materials &&
      configuringPendingProductData?.model?.typeId === imageData?.model
    ) {
      log(
        "PDPProductImages | pendingProductData change???",
        configuringPendingProductData.model.typeId
      );
      // 3107FU-{{FullUpholstered}}-{{Base}}-r{{angle}}.{{format}}
      // Render?model=3107FU&angle=0&FullUpholstered=HAL840&Base=DRBL&Format=jpeg&bgcolor=ffffff&size=1024

      let materialMap = new Map([["format", webPSupport ? "webp" : "jpg"]]);

      // find all material angles and push to an array
      const activeAngles = configuringPendingProductData.materials.reduce(
        (acc, val, index) => {
          if (imageData?.layers[index]?.materials?.length) {
            const layer = imageData.layers[index];
            const mObj = layer.materials.filter(
              (it) => it.id === val.activeMaterial.materialId
            );
            if (mObj.length >= 1 && mObj[0].angles) {
              // create material mapping
              materialMap.set(layer.name, val.activeMaterial.materialId);
              // active angles
              acc.push(mObj[0].angles);
            }
          }
          return acc;
        },
        []
      );
      if (!activeAngles || activeAngles.length === 0) return;
      // find common values in angles array
      const commonImageAngles = activeAngles?.reduce((a, b) => {
        return a.filter((c) => b.includes(c)).sort((a, b) => a - b);
      });
      const qs = placeholderReplace(
        imageData.querytemplate,
        materialMap,
        "{{$}}"
      );

      if (!commonImageAngles || commonImageAngles.length === 0) return;
      const _images = commonImageAngles.map((angle) => {
        const angleObj = { angle };
        if (!activeProductData.UseImageCDNUrl || !configuringPendingProductData.UseImageCDNUrl) {
          return `${modelImageUrl}${placeholderReplace(
            qs,
            angleObj,
            "{{$}}"
          )}?bgcolor=efeeeb`;
        } else {
          return `${modelImageUrl}${placeholderReplace(
            qs,
            angleObj,
            "{{$}}"
          )}?bgcolor=efeeeb`;
        }
      });
      setImages(_images);
    }
  }, [configuringPendingProductData, activeProductData, imageData, webPSupport]);

  useEffect(() => {
    const a = document.createElement("a");
    if (a.relList.supports("ar")) {
      setIsRelAr(true);
    }
  }, []);
  useEffect(() => {
    if (activeProductData && activeProductData.model) {
      let newUrl = getWebarImgUrl(activeProductData, isRelAR);
      log("set new web AR url:", newUrl);
      setWebarUrl(newUrl);
    }
  }, [activeProductData, isRelAR]);

  useEffect(() => {
    if(activeConfigurationSKU){
      if(swiperInstance?.current?.swiper){
        swiperInstance.current.swiper.slideToLoop(0, 300);
      }
      setActiveImageIndex(0);
      setActiveStyledImage(null);
    }
  }, [activeConfigurationSKU, swiperInstance])

  const getWebarImgUrl = (prodData, isRel) => {
    if (!prodData || !prodData.model) return false;
    if (!prodData.UseImageCDNUrl) {
      return `${process.env.REACT_APP_PRODUCT_IMAGE_API}ar/${prodData.model.configurationId
        }.${isRel ? "usdz" : "html"}`;
    }

    return `${process.env.REACT_APP_PRODUCT_IMAGE_API}ar/${prodData.model.configurationId
      }.${isRel ? "usdz" : "html"}`;
  };

  const handleZoom = (relPos, currentFrame, frameNode) => {
    setZoom({ zooming: true, relPos, currentFrame, frameNode });
  };
  const handleStopZoom = () => {
    setZoom({ zooming: false });
  };

  const handleImageLoadUpdate = (percent) => {
    setImageProgress(percent);
  };

  const imageIsRendering = () => {
    return activeImageIndex === 0 && !skuImageUrl;
  };

  const showQrCode = () => {
    return activeImageIndex === 0 && !(configuratorMode === CONFIGURATOR_MODE.NONE);
  };

  const viewQRCodeClickHandler = () => {
    if (activeConfigurationSKU) {
      setShowQRCodeModal(true);
      dataLayerPush({
        event: "trackWatchAtHome",
        productName: activeProductData?.dataLayerProductName,
        productDestination: activeProductData?.urlSlug
      });
    }
  };

  const carouselId = getUniqueId(true);

  const imagesClassList = classnames({
    "pdp-product-images": true,
    "is-rendering": !!imageIsRendering(),
    "styled-image-is-active": styledImageIsActive
  });

  const downloadMobileClassList = classnames({
    "pdp-product-images__download-mobile": true,
    "is-styled-image": !imageIsRendering()
  });

  const carouselClassList = classnames({
    "pdp-product-images__carousel": true,
    "zoom-active": !!zoom?.zooming,
    "is-unique-material": activeUniqueMaterial
  });

  return (
    <div className={imagesClassList}>
      <div className={downloadMobileClassList}>
        <PDPDownloadButton
          isRendering={imageIsRendering()}
          customImageUrl={activeStyledImage}
        />
      </div>
      {webArUrl && imageData && imageData.webar && imageIsRendering() && (
        <div className="pdp-product-images__webar">
          <a
            rel={isRelAR ? "ar" : "noreferrer"}
            target={isRelAR ? undefined : "_blank"}
            href={webArUrl}
          >
            <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==" />
            <span>{texts.webArText}</span>
          </a>
          {/* <WebArSvg aria-labelledby="webar" /> */}
          <span id="webar" className="sr-only">
            {texts.webVr}
          </span>
        </div>
      )}

      <div className={carouselClassList}>
        {(!activeProductData?.styledImages ||
          activeProductData?.styledImages?.length === 0 ||
          activeSelector !== CONFIGURATOR_SELECTOR.NONE) && (
          <PDPProductImageSpinner
            images={skuImageUrl ? [skuImageUrl] : images}
            onZoom={handleZoom}
            onUpdate={handleImageLoadUpdate}
          />
        )}
        {activeProductData?.styledImages?.length > 0 &&
          activeSelector === CONFIGURATOR_SELECTOR.NONE && (
            <>
              <button
                className="pdp-product-images__carousel__prev"
                id={`${carouselId}SliderPrev`}
              >
                <ArrowSmallSvg />
              </button>
              <Swiper
                ref={swiperInstance}
                slidesPerView={1}
                direction="horizontal"
                className="pdp-product-images__swiper-wrapper"
                loop={true}
                allowTouchMove={false}
                spaceBetween={100}
                onAfterInit={(args) => {
                  setSwiperInitialized(true);
                  if (onSliderChange) onSliderChange(0);
                }}
                onSlideChange={(args) => {
                  //for some reason, the real index start at 1 instead of 0 during initialization. Make sure the index starts at 0.
                  if (!swiperInitialized) {
                    setActiveImageIndex(0);
                    setStyledImageIsActive(false);
                  } else {
                    setActiveImageIndex(args.realIndex);
                    if (args.realIndex > 0) {
                      setStyledImageIsActive(true);
                    } else {
                      setStyledImageIsActive(false);
                    }
                  }
                  if (args.realIndex > 0 && activeProductData?.styledImages) {
                    setActiveStyledImage(
                      activeProductData.styledImages[args.realIndex - 1]
                    );
                  } else {
                    setActiveStyledImage(null);
                  }
                  if (onSliderChange) onSliderChange(args.realIndex);
                }}
                navigation={{
                  nextEl: `#${carouselId}SliderNext`,
                  prevEl: `#${carouselId}SliderPrev`,
                  disabledClass: "is-disabled",
                }}
              >
                <SwiperSlide className="pdp-product-images_styled-image-slide">
                  <PDPProductImageSpinner
                    images={skuImageUrl ? [skuImageUrl] : images}
                    onZoom={handleZoom}
                    onUpdate={handleImageLoadUpdate}
                  />
                </SwiperSlide>
                {activeProductData.styledImages &&
                  activeProductData.styledImages.map((imageUrl, index) => {
                    return (
                      <SwiperSlide
                        key={`styled-image${index}`}
                        className="pdp-product-images_styled-image-slide"
                      >
                        <PDPProductImageSpinner
                          images={[imageUrl]}
                          onZoom={handleZoom}
                          onUpdate={handleImageLoadUpdate}
                        />
                      </SwiperSlide>
                    );
                  })}
              </Swiper>
              <button
                className="pdp-product-images__carousel__next"
                id={`${carouselId}SliderNext`}
              >
                <ArrowSmallSvg />
              </button>
            </>
          )}
      </div>

      <PDPProductImageZoom
        {...zoom}
        images={
          activeImageIndex > 0 && activeProductData?.styledImages
            ? activeProductData.styledImages[activeImageIndex - 1]
            : skuImageUrl
            ? [skuImageUrl]
            : images
        }
        onStopZoom={handleStopZoom}
        resolutions={
          (activeImageIndex > 0 && activeProductData?.styledImages) ||
          skuImageUrl
            ? [1024, 2500]
            : imageData?.resolutions
        }
        isRendering={imageIsRendering()}
      />

      <div className="pdp-product-viewer__guide">
        {imageProgress < 1 && imageProgress > 0 && (
          <progress
            className="pdp-product-images__progress"
            max="1"
            value={imageProgress}
          />
        )}
        {imageProgress === 1 && (
          <React.Fragment>
            {showQrCode() && imageIsRendering() && (
              <div>
                <button
                  className="pdp-webar-button"
                  onClick={viewQRCodeClickHandler}
                >
                  <span className="text">{texts.webArText}</span>
                  {/* <span className="icon">
                  <WebArSvg aria-labelledby="webar" />
                </span> */}
                </button>
              </div>
            )}
            <PDPDownloadButton
              isRendering={imageIsRendering()}
              customImageUrl={activeStyledImage}
            />
            <div>
              <span>{texts.clickToZoom}</span>
            </div>
            {imageIsRendering() && (
              <div>
                <span>{texts.dragToRotate}</span>
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

export default PDPProductImages;
